import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Box, Typography, Grid, Accordion, AccordionSummary, AccordionDetails,
  Avatar, Divider, Chip, Card, CardContent, Paper, IconButton, Tooltip,
  useTheme, useMediaQuery, Skeleton, Fade, Zoom, Stack, Badge
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { addPdfDisclaimer } from '../utils/pdfDisclaimer';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api from '../api/axiosConfig';
import { canModifyRecord } from '../utils/permissions';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HeightIcon from '@mui/icons-material/Height';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import EditableStatField from './EditableStatField';
import EditableTextField from './EditableTextField';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import SecurityIcon from '@mui/icons-material/SecurityOutlined';
import SportsIcon from '@mui/icons-material/Sports';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SchoolIcon from '@mui/icons-material/School';
import InfoIcon from '@mui/icons-material/Info';
import MovieIcon from '@mui/icons-material/Movie';
import MediaTechnica from './MediaTechnica';

// Chart.js imports avec alias pour éviter les conflits
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  Title,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Configuration et enregistrement Chart.js
try {
  ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    ChartTooltip,
    Legend,
    Title,
    Filler
  );
} catch (error) {
  console.error('Error registering Chart.js components:', error);
}

// Styled Components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: '16px !important',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  overflow: 'hidden',
  border: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
    transform: 'translateY(-2px)',
  },
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: theme.spacing(2, 0),
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.primary.main}`,
  minHeight: '72px',
  '& .MuiAccordionSummary-content': {
    margin: '16px 0',
  },
  '&.Mui-expanded': {
    minHeight: '72px',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  border: '1px solid rgba(0,0,0,0.08)',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
}));

const SeasonChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  borderRadius: '30px',
  padding: '12px 24px',
  fontWeight: 'bold',
  fontSize: '1.1rem',
  boxShadow: '0 2px 10px rgba(234, 127, 0, 0.3)',
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    color: theme.palette.primary.main,
  },
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
}));

const ChartContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: '16px',
  height: '400px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  marginBottom: theme.spacing(3),
}));

// Helper functions
const getSeasonYear = (date) => {
  const d = new Date(date);
  const month = d.getMonth();
  const year = d.getFullYear();
  return month >= 7 ? year : year - 1;
};

const getSeasonLabel = (year, t) => `${t('playerCardCreator.tecnicaTab.season')} ${year}-${year + 1}`;

const getAvatarUrl = (userId) => {
  return `${process.env.REACT_APP_MEDIA_URL}/users/${userId}.png?${new Date().getTime()}`;
};

const convertHtmlToText = (html) => {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  function traverse(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.nodeValue;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const tag = node.tagName.toLowerCase();
      let text = '';

      if (tag === 'br') return '\n';
      if (tag === 'p' || tag === 'div') {
        node.childNodes.forEach((child) => {
          text += traverse(child);
        });
        text += ' ';
        return text;
      }
      if (tag === 'ol') {
        let index = 1;
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'li') {
            text += index + '. ' + traverse(child) + '\n';
            index++;
          } else {
            text += traverse(child);
          }
        });
        return text;
      }
      if (tag === 'ul') {
        node.childNodes.forEach((child) => {
          if (child.nodeType === Node.ELEMENT_NODE && child.tagName.toLowerCase() === 'li') {
            text += '• ' + traverse(child) + '\n';
          } else {
            text += traverse(child);
          }
        });
        return text;
      }
      if (tag === 'li') {
        text += Array.from(node.childNodes).map((child) => traverse(child)).join('');
        return text;
      }

      node.childNodes.forEach((child) => {
        text += traverse(child);
      });
      return text;
    }
    return '';
  }

  let result = traverse(doc.body);
  result = result.replace(/\n{2,}/g, '\n').trim();
  return result;
};

const addPageTemplate = (doc, clubName = 'Club', departmentText = 'DEPARTAMENTO DE SCOUTING', primaryColor = '#EA7F00') => {
  const pageNumber = doc.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Convert hex color to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 234, g: 127, b: 0 };
  };

  const rgb = hexToRgb(primaryColor);

  doc.setDrawColor(primaryColor);
  doc.setLineWidth(2);
  doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

  const bandHeight = 20;
  const bandY = pageHeight - 10 - bandHeight;
  doc.setFillColor(rgb.r, rgb.g, rgb.b);
  doc.rect(10, bandY, pageWidth - 20, bandHeight, 'F');

  const logoSize = 16;
  try {
    const logoY = bandY + (bandHeight - logoSize) / 2;

    // Déterminer les logos selon l'URL de l'application
    const currentUrl = window.location.href.toLowerCase();
    const isKansas = currentUrl.includes('kansas');
    const isMetropolitans = currentUrl.includes('metropolitans');
    const isByers = currentUrl.includes('byers');
    const leftLogo = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/LogoValenciaBasket.png`;
    const rightLogo = isKansas ? `${process.env.PUBLIC_URL}/assets/kansas.png`
      : isMetropolitans ? `${process.env.PUBLIC_URL}/assets/metropolitans.png`
      : isByers ? `${process.env.PUBLIC_URL}/assets/byers.png`
      : `${process.env.PUBLIC_URL}/assets/alqueria-del-basket.png`;

    doc.addImage(
      leftLogo,
      'PNG',
      15,
      logoY,
      logoSize,
      logoSize
    );
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(9);
    doc.text(
      `${clubName.toUpperCase()} - ${departmentText}`,
      pageWidth / 2,
      bandY + bandHeight / 2 + 3,
      { align: 'center' }
    );
    doc.addImage(
      rightLogo,
      'PNG',
      pageWidth - 15 - logoSize,
      logoY,
      logoSize,
      logoSize
    );
    doc.setFontSize(8);
    doc.text(pageNumber.toString(), pageWidth / 2, bandY + bandHeight - 3, { align: 'center' });
  } catch (error) {
    console.error('Error adding images to PDF page:', error);
  }

  doc.setTextColor(0);
  doc.setFontSize(12);
  addPdfDisclaimer(doc);
};

const TechnicaTab = ({ playerId, dateNaissance, nom, onDataLoaded }) => {
  const { t } = useTranslation();
  const [techData, setTechData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [groupedData, setGroupedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [ficheToDelete, setFicheToDelete] = useState(null);
  const [currentUser] = useState(() => JSON.parse(localStorage.getItem('user') || 'null'));

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const handleDeleteClick = (fiche) => {
    setFicheToDelete(fiche);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setFicheToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (ficheToDelete) {
      try {
        await api.delete(`/api/technique/${ficheToDelete.id}`);
        fetchTechData();
        handleClose();
      } catch (error) {
        console.error("Error al eliminar la ficha técnica:", error);
      }
    }
  };

  const fetchTechData = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/technique', {
        params: {
          playerId
        }
      });
      const data = response.data;
      setTechData(data);

      if (data.length >= 2 && dateNaissance) {
        const birthDate = new Date(dateNaissance);
        const processedData = data
          .map(item => {
            const measurementDate = new Date(item.fecha);
            const ageAtMeasurement =
              (measurementDate - birthDate) / (1000 * 60 * 60 * 24 * 365.25);
            return {
              age: parseFloat(ageAtMeasurement.toFixed(1)),
              altura_sin_zapatos: item.altura_sin_zapatos,
              peso_kg: item.peso_kg,
              date: measurementDate.toLocaleDateString()
            };
          })
          .sort((a, b) => a.age - b.age);
        setChartData(processedData);
      }

      const grouped = data.reduce((acc, item) => {
        const seasonYear = getSeasonYear(item.fecha);
        if (!acc[seasonYear]) {
          acc[seasonYear] = [];
        }
        acc[seasonYear].push(item);
        return acc;
      }, {});

      Object.keys(grouped).forEach(season => {
        grouped[season].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      });

      setGroupedData(grouped);

      if (onDataLoaded) {
        onDataLoaded(data.length > 0);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des données techniques:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTechData();
  }, [playerId, onDataLoaded, dateNaissance]);

  const generatePDF = async (item) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      const marginLeft = 20;
      const marginRight = 20;
      let yPosition = 20;
      const lineHeight = 7;
      const contentWidth = pageWidth - marginLeft - marginRight;

      // Get club info from localStorage
      const clubName = localStorage.getItem('clubName') || 'Club';
      const clubThemeStr = localStorage.getItem('clubTheme');
      let primaryColor = '#EA7F00';
      if (clubThemeStr) {
        try {
          const clubTheme = JSON.parse(clubThemeStr);
          primaryColor = clubTheme.primaryColor || '#EA7F00';
        } catch (e) {
          console.error('Error parsing clubTheme:', e);
        }
      }
      const departmentText = t('pdf.footer.department') || 'DEPARTAMENTO DE SCOUTING';

      // Convert hex color to RGB for use in document
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 234, g: 127, b: 0 };
      };
      const rgb = hexToRgb(primaryColor);

      addPageTemplate(doc, clubName, departmentText, primaryColor);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');

      const addNewPageIfNeeded = (neededSpace = 20) => {
        if (yPosition + neededSpace > pageHeight - 20) {
          doc.addPage();
          addPageTemplate(doc, clubName, departmentText, primaryColor);
          yPosition = 20;
        }
      };

      try {
        const avatarUrl = `${process.env.REACT_APP_MEDIA_URL}/photos/${playerId}.png`;
        const response = await fetch(
          `/api/proxy-image?url=${encodeURIComponent(avatarUrl)}`
        );
        const blob = await response.blob();
        const imgData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
        doc.addImage(imgData, 'PNG', marginLeft, yPosition, 30, 30);
      } catch (err) {
        console.error('Erreur lors du chargement de la photo:', err);
      }

      doc.setFontSize(24);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(t('pdf.sections.technicalSheet') || 'Ficha Técnica', pageWidth / 2, yPosition + 15, { align: 'center' });
      doc.setFontSize(18);
      doc.setTextColor(0);
      doc.text(nom, pageWidth / 2, yPosition + 30, { align: 'center' });
      doc.setFontSize(12);
      yPosition += 40;

      doc.text(`${t('pdf.labels.date') || 'Fecha'}: ${new Date(item.fecha).toLocaleDateString()}`, marginLeft, yPosition);
      doc.text(`${t('pdf.labels.coach') || 'Entrenador'}: ${item.usuario_nombre}`, marginLeft, yPosition + lineHeight);
      yPosition += 20;

      doc.setFontSize(16);
      doc.setTextColor(rgb.r, rgb.g, rgb.b);
      doc.text(t('pdf.sections.physicalMeasurements') || 'Medidas Físicas', marginLeft, yPosition);
      doc.setFontSize(12);
      doc.setTextColor(0);
      yPosition += 10;

      doc.autoTable({
        startY: yPosition,
        head: [[t('pdf.labels.measurement') || 'Medida', t('pdf.labels.value') || 'Valor']],
        body: [
          [t('pdf.labels.weight') || 'Peso', item.peso_kg ? `${item.peso_kg} kg` : '-'],
          ['Body Fat', item.body_fat_porcentaje ? `${item.body_fat_porcentaje}%` : '-'],
          [t('pdf.labels.heightWithoutShoes') || 'Altura s/ Zapatos', item.altura_sin_zapatos ? `${item.altura_sin_zapatos} cm` : '-'],
          [t('pdf.labels.heightWithShoes') || 'Altura c/ Zapatos', item.altura_con_zapatos ? `${item.altura_con_zapatos} cm` : '-'],
          [t('pdf.labels.verticalWingspan') || 'Envergadura Vertical', item.entrega_vertical ? `${item.entrega_vertical} cm` : '-'],
          [t('pdf.labels.horizontalWingspan') || 'Envergadura Horizontal', item.entrega_horizontal ? `${item.entrega_horizontal} cm` : '-'],
        ],
        theme: 'striped',
        headStyles: {
          fillColor: [rgb.r, rgb.g, rgb.b],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: 'linebreak',
        },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.7 },
          1: { cellWidth: contentWidth * 0.3, halign: 'center' },
        },
        margin: { left: marginLeft, right: marginRight },
        didDrawPage: function () {
          addPageTemplate(doc, clubName, departmentText, primaryColor);
        },
      });
      yPosition = doc.lastAutoTable.finalY + 10;

      const printSectionText = (title, fields) => {
        addNewPageIfNeeded(30);
        doc.setFontSize(14);
        doc.setTextColor(rgb.r, rgb.g, rgb.b);
        doc.setFont('helvetica', 'bold');
        doc.text(title, marginLeft, yPosition);
        yPosition += 10;
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont('helvetica', 'normal');

        fields.forEach((field) => {
          addNewPageIfNeeded(20);
          const label = field.label;
          let value = field.value ? field.value.trim() : '';
          value = convertHtmlToText(value);
          const displayValue = value && value.length > 0 ? value : '-';

          doc.setFont('helvetica', 'bold');
          doc.text(`${label}:`, marginLeft, yPosition);
          yPosition += 6;

          doc.setFont('helvetica', 'normal');
          const textLines = doc.splitTextToSize(displayValue, contentWidth);
          textLines.forEach(line => {
            addNewPageIfNeeded(10);
            doc.text(line, marginLeft, yPosition);
            yPosition += 6;
          });
          yPosition += 4;
        });
      };

      printSectionText('Medidas Físicas (Comentarios)', [
        { label: 'Comentarios y Observaciones', value: item.comentarios_observaciones_medidas },
        { label: 'Escala Perfil Atlético', value: item.escala_perfil_atletico },
        { label: 'Informe Medical', value: item.informe_medico },
      ]);

      printSectionText('Ataque - Aspectos Individuales', [
        { label: 'Puntos Fuertes', value: item.ataque_asp_ind_puntos_fuertes },
        { label: 'Objetivos de Mejora', value: item.ataque_asp_ind_objetivos_mejora },
        { label: 'Observaciones Individuales', value: item.ataque_asp_ind_observaciones_individuales },
      ]);

      printSectionText('Ataque - Aspectos Colectivos', [
        { label: 'Puntos Fuertes', value: item.ataque_asp_col_puntos_fuertes },
        { label: 'Objetivos de Mejora', value: item.ataque_asp_col_objetivos_mejora },
        { label: 'Observaciones Colectivas', value: item.ataque_asp_col_observaciones_colectivas },
      ]);

      printSectionText('Defensa - Aspectos Individuales', [
        { label: 'Puntos Fuertes', value: item.defensa_asp_ind_puntos_fuertes },
        { label: 'Objetivos de Mejora', value: item.defensa_asp_ind_objetivos_mejora },
        { label: 'Observaciones Individuales', value: item.defensa_asp_ind_observaciones_individuales },
      ]);

      printSectionText('Defensa - Aspectos Colectivos', [
        { label: 'Puntos Fuertes', value: item.defensa_asp_col_puntos_fuertes },
        { label: 'Objetivos de Mejora', value: item.defensa_asp_col_objetivos_mejora },
        { label: 'Observaciones Colectivas', value: item.defensa_asp_col_observaciones_colectivas },
      ]);

      printSectionText('Información Audiovisual', [
        { label: 'Observaciones', value: item.info_audiovisual_observaciones },
      ]);

      printSectionText('Aspectos Físicos', [
        { label: 'Puntos Fuertes', value: item.asp_fisicos_puntos_fuertes },
        { label: 'Aspectos que Potenciar', value: item.asp_fisicos_aspectos_potenciar },
        { label: 'Desarrollo motriz', value: item.asp_fisicos_desarrollo_motriz },
        { label: 'Observaciones', value: item.asp_fisicos_observaciones },
      ]);

      printSectionText('Aspectos Psicológicos', [
        { label: 'Observaciones', value: item.asp_psicologicos_observaciones },
      ]);

      printSectionText('Ámbito Académico', [
        { label: 'Observaciones', value: item.ambito_academico_observaciones },
      ]);

      printSectionText('INTEL', [
        { label: 'Observaciones', value: item.intel_observaciones },
      ]);

      doc.save(
        `${t('pdf.filename.technicalSheet') || 'ficha-tecnica'}-${clubName}-${nom}-${new Date(item.fecha).toLocaleDateString().replace(/\//g, '-')}.pdf`
      );

    } catch (error) {
      console.error('Erreur PDF:', error);
    }
  };

  // Modern Chart Component
  const ModernChart = ({ type }) => {
    const filteredData = chartData.filter(d => type === 'height' ? d.altura_sin_zapatos : d.peso_kg);
    if (filteredData.length < 2) return null;

    const labels = filteredData.map(d => d.age);
    const dataValues = filteredData.map(d => type === 'height' ? d.altura_sin_zapatos : d.peso_kg);

    const gradientColor = type === 'height' ? 
      ['rgba(255, 152, 0, 0.8)', 'rgba(255, 152, 0, 0.2)'] : 
      ['rgba(33, 150, 243, 0.8)', 'rgba(33, 150, 243, 0.2)'];

    const data = {
      labels,
      datasets: [
        {
          label: type === 'height' ? t('playerCardCreator.tecnicaTab.heightWithoutShoes') : t('playerCardCreator.tecnicaTab.weight'),
          data: dataValues,
          borderColor: type === 'height' ? theme.palette.primary.main : theme.palette.info.main,
          backgroundColor: (context) => {
            const ctx = context.chart.ctx;
            const gradient = ctx.createLinearGradient(0, 0, 0, 300);
            gradient.addColorStop(0, gradientColor[0]);
            gradient.addColorStop(1, gradientColor[1]);
            return gradient;
          },
          tension: 0.4,
          fill: true,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: '#fff',
          pointBorderWidth: 3,
        }
      ]
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: type === 'height' ? t('playerCardCreator.tecnicaTab.heightEvolutionTitle') : t('playerCardCreator.tecnicaTab.weightEvolutionTitle'),
          font: {
            size: 16,
            weight: 'bold'
          },
          padding: 20
        },
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(0,0,0,0.8)',
          padding: 12,
          cornerRadius: 8,
          titleFont: {
            size: 14
          },
          bodyFont: {
            size: 13
          }
        }
      },
      scales: {
        y: {
          title: {
            display: true,
            text: type === 'height' ? t('playerCardCreator.tecnicaTab.heightCm') : t('playerCardCreator.tecnicaTab.weightKg'),
            font: {
              size: 14
            }
          },
          grid: {
            color: 'rgba(0,0,0,0.05)'
          }
        },
        x: {
          title: {
            display: true,
            text: t('playerCardCreator.tecnicaTab.ageYears'),
            font: {
              size: 14
            }
          },
          grid: {
            color: 'rgba(0,0,0,0.05)'
          }
        }
      }
    };

    return (
      <ChartContainer elevation={0}>
        <Line data={data} options={options} />
      </ChartContainer>
    );
  };

  // Section Components
  const renderMedidasFisicas = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <BarChartIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.physicalMeasurements')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item, index) => (
          <Fade in={true} timeout={300 * index} key={item.id}>
            <StyledCard elevation={0}>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                  <Chip
                    icon={<CalendarTodayIcon />}
                    label={new Date(item.fecha).toLocaleDateString()}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    avatar={<Avatar src={getAvatarUrl(item.usuario_id)} sx={{ width: 24, height: 24 }} />}
                    label={item.usuario_nombre}
                    variant="outlined"
                  />
                </Stack>

                <Grid container spacing={2}>
                  {[
                    { field: 'peso_kg', label: t('playerCardCreator.tecnicaTab.weightLabel'), icon: FitnessCenterIcon, unit: 'kg' },
                    { field: 'body_fat_porcentaje', label: t('playerCardCreator.tecnicaTab.bodyFat'), icon: FitnessCenterIcon, unit: '%' },
                    { field: 'altura_con_zapatos', label: t('playerCardCreator.tecnicaTab.heightWithShoesLabel'), icon: HeightIcon, unit: 'cm' },
                    { field: 'altura_sin_zapatos', label: t('playerCardCreator.tecnicaTab.heightWithoutShoesLabel'), icon: HeightIcon, unit: 'cm' },
                    { field: 'entrega_vertical', label: t('playerCardCreator.tecnicaTab.verticalWingspan'), icon: AccessibilityNewIcon, unit: 'cm' },
                    { field: 'entrega_horizontal', label: t('playerCardCreator.tecnicaTab.horizontalWingspan'), icon: AccessibilityNewIcon, unit: 'cm' },
                  ].map((stat) => (
                    <Grid item xs={12} sm={6} md={4} key={stat.field}>
                      <StatCard elevation={0}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {stat.label}
                        </Typography>
                        <EditableStatField
                          item={item}
                          fieldName={stat.field}
                          icon={stat.icon}
                          unit={stat.unit}
                          playerId={playerId}
                          onUpdate={fetchTechData}
                          size="medium"
                        />
                      </StatCard>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ mt: 3 }}>
                  <Grid container spacing={2}>
                    {[
                      { field: 'comentarios_observaciones_medidas', label: t('playerCardCreator.tecnicaTab.commentsObservations') },
                      { field: 'escala_perfil_atletico', label: t('playerCardCreator.tecnicaTab.athleticProfileScale') },
                      { field: 'informe_medico', label: t('playerCardCreator.tecnicaTab.medicalReport') },
                    ].map((field) => (
                      <Grid item xs={12} key={field.field}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {field.label}
                        </Typography>
                        <EditableTextField
                          item={item}
                          fieldName={field.field}
                          playerId={playerId}
                          onUpdate={fetchTechData}
                          variant="outlined"
                          fullWidth
                          multiline
                          rows={3}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </StyledCard>
          </Fade>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderAtaque = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <SportsIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.attack')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {[t('playerCardCreator.tecnicaTab.individualAspects'), t('playerCardCreator.tecnicaTab.collectiveAspects')].map((aspect, aspectIndex) => (
            <Grid item xs={12} key={aspect}>
              <StyledCard elevation={0}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    {aspect}
                  </Typography>
                  {seasonData.map((item, index) => (
                    <Box key={item.id} sx={{ mb: 3 }}>
                      <Chip
                        icon={<CalendarTodayIcon />}
                        label={new Date(item.fecha).toLocaleDateString()}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.strengths')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "ataque_asp_ind_puntos_fuertes" : "ataque_asp_col_puntos_fuertes"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.improvementGoals')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "ataque_asp_ind_objetivos_mejora" : "ataque_asp_col_objetivos_mejora"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.observations')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "ataque_asp_ind_observaciones_individuales" : "ataque_asp_col_observaciones_colectivas"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                      </Grid>
                      {index < seasonData.length - 1 && <Divider sx={{ mt: 3 }} />}
                    </Box>
                  ))}
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderDefensa = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <SecurityIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.defense')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {[t('playerCardCreator.tecnicaTab.individualAspects'), t('playerCardCreator.tecnicaTab.collectiveAspects')].map((aspect, aspectIndex) => (
            <Grid item xs={12} key={aspect}>
              <StyledCard elevation={0}>
                <CardContent>
                  <Typography variant="h6" gutterBottom color="primary">
                    {aspect}
                  </Typography>
                  {seasonData.map((item, index) => (
                    <Box key={item.id} sx={{ mb: 3 }}>
                      <Chip
                        icon={<CalendarTodayIcon />}
                        label={new Date(item.fecha).toLocaleDateString()}
                        size="small"
                        sx={{ mb: 2 }}
                      />
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.strengths')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "defensa_asp_ind_puntos_fuertes" : "defensa_asp_col_puntos_fuertes"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.improvementGoals')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "defensa_asp_ind_objetivos_mejora" : "defensa_asp_col_objetivos_mejora"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            {t('playerCardCreator.tecnicaTab.observations')}
                          </Typography>
                          <EditableTextField
                            item={item}
                            fieldName={aspectIndex === 0 ? "defensa_asp_ind_observaciones_individuales" : "defensa_asp_col_observaciones_colectivas"}
                            playerId={playerId}
                            onUpdate={fetchTechData}
                            variant="outlined"
                            fullWidth
                            multiline
                            rows={3}
                          />
                        </Grid>
                      </Grid>
                      {index < seasonData.length - 1 && <Divider sx={{ mt: 3 }} />}
                    </Box>
                  ))}
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderInfoAudiovisual = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <MovieIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.audiovisualInfo')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item, index) => (
          <StyledCard key={item.id} elevation={0}>
            <CardContent>
              <Chip
                icon={<CalendarTodayIcon />}
                label={new Date(item.fecha).toLocaleDateString()}
                avatar={<Avatar src={getAvatarUrl(item.usuario_id)} sx={{ width: 24, height: 24 }} />}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('playerCardCreator.tecnicaTab.observations')}
              </Typography>
              <EditableTextField
                item={item}
                fieldName="info_audiovisual_observaciones"
                playerId={playerId}
                onUpdate={fetchTechData}
                variant="outlined"
                fullWidth
                multiline
                rows={3}
              />
              <Box sx={{ mt: 3 }}>
                <MediaTechnica itemId={item.id} />
              </Box>
            </CardContent>
          </StyledCard>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderAspectosFisicos = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <FitnessCenterIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.physicalAspects')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item, index) => (
          <StyledCard key={item.id} elevation={0}>
            <CardContent>
              <Chip
                icon={<CalendarTodayIcon />}
                label={new Date(item.fecha).toLocaleDateString()}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                {[
                  { field: 'asp_fisicos_puntos_fuertes', label: t('playerCardCreator.tecnicaTab.strengths') },
                  { field: 'asp_fisicos_aspectos_potenciar', label: t('playerCardCreator.tecnicaTab.aspectsToEnhance') },
                  { field: 'asp_fisicos_desarrollo_motriz', label: t('playerCardCreator.tecnicaTab.motorDevelopment') },
                  { field: 'asp_fisicos_observaciones', label: t('playerCardCreator.tecnicaTab.observations') },
                ].map((field) => (
                  <Grid item xs={12} md={6} key={field.field}>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {field.label}
                    </Typography>
                    <EditableTextField
                      item={item}
                      fieldName={field.field}
                      playerId={playerId}
                      onUpdate={fetchTechData}
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </StyledCard>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderAspectosPsicologicos = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <PsychologyIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.psychologicalAspects')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item) => (
          <StyledCard key={item.id} elevation={0}>
            <CardContent>
              <Chip
                icon={<CalendarTodayIcon />}
                label={new Date(item.fecha).toLocaleDateString()}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('playerCardCreator.tecnicaTab.observations')}
              </Typography>
              <EditableTextField
                item={item}
                fieldName="asp_psicologicos_observaciones"
                playerId={playerId}
                onUpdate={fetchTechData}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
              />
            </CardContent>
          </StyledCard>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderAmbitoAcademico = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <SchoolIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.academicScope')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item) => (
          <StyledCard key={item.id} elevation={0}>
            <CardContent>
              <Chip
                icon={<CalendarTodayIcon />}
                label={new Date(item.fecha).toLocaleDateString()}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('playerCardCreator.tecnicaTab.observations')}
              </Typography>
              <EditableTextField
                item={item}
                fieldName="ambito_academico_observaciones"
                playerId={playerId}
                onUpdate={fetchTechData}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
              />
            </CardContent>
          </StyledCard>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  const renderIntel = (seasonData, isLatestSeason) => (
    <StyledAccordion defaultExpanded={isLatestSeason}>
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <SectionHeader>
          <InfoIcon />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('playerCardCreator.tecnicaTab.intel')}
          </Typography>
        </SectionHeader>
      </StyledAccordionSummary>
      <AccordionDetails sx={{ p: 3 }}>
        {seasonData.map((item) => (
          <StyledCard key={item.id} elevation={0}>
            <CardContent>
              <Chip
                icon={<CalendarTodayIcon />}
                label={new Date(item.fecha).toLocaleDateString()}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" gutterBottom>
                {t('playerCardCreator.tecnicaTab.observations')}
              </Typography>
              <EditableTextField
                item={item}
                fieldName="intel_observaciones"
                playerId={playerId}
                onUpdate={fetchTechData}
                variant="outlined"
                fullWidth
                multiline
                rows={4}
              />
            </CardContent>
          </StyledCard>
        ))}
      </AccordionDetails>
    </StyledAccordion>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {[1, 2, 3].map((i) => (
            <Grid item xs={12} key={i}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!Object.keys(groupedData).length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          {t('playerCardCreator.tecnicaTab.noDataAvailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
      {/* Charts Section */}
      {chartData.length >= 2 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            {t('playerCardCreator.tecnicaTab.physicalEvolution')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <ModernChart type="height" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <ModernChart type="weight" />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Seasons Data */}
      {Object.entries(groupedData)
        .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
        .map(([year, seasonData], index) => {
          const isLatestSeason = index === 0;
          return (
            <Zoom in={true} timeout={300 * index} key={year}>
              <StyledAccordion defaultExpanded={isLatestSeason}>
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    backgroundColor: theme.palette.grey[50],
                    '&:hover': {
                      backgroundColor: theme.palette.grey[100],
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <SeasonChip>
                      <CalendarTodayIcon sx={{ mr: 1 }} />
                      {getSeasonLabel(Number(year), t)}
                    </SeasonChip>
                    <Box sx={{ ml: 'auto', mr: 2 }}>
                      <Badge badgeContent={seasonData.length} color="primary">
                        <Typography variant="body2" color="text.secondary">
                          {t('playerCardCreator.tecnicaTab.sheets')}
                        </Typography>
                      </Badge>
                    </Box>
                  </Box>
                </StyledAccordionSummary>
                <AccordionDetails sx={{ p: 0, backgroundColor: theme.palette.grey[50] }}>
                  <Box sx={{ p: 3 }}>
                    {renderMedidasFisicas(seasonData, isLatestSeason)}
                    {renderAtaque(seasonData, isLatestSeason)}
                    {renderDefensa(seasonData, isLatestSeason)}
                    {renderInfoAudiovisual(seasonData, isLatestSeason)}
                    {renderAspectosFisicos(seasonData, isLatestSeason)}
                    {renderAspectosPsicologicos(seasonData, isLatestSeason)}
                    {renderAmbitoAcademico(seasonData, isLatestSeason)}
                    {renderIntel(seasonData, isLatestSeason)}

                    {/* Actions Section */}
                    <Paper sx={{ p: 3, mt: 3, borderRadius: 2 }} elevation={0}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                        {t('playerCardCreator.tecnicaTab.actions')}
                      </Typography>
                      <Grid container spacing={2}>
                        {seasonData.map((item) => (
                          <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <Card sx={{ p: 2 }} elevation={0}>
                              <Stack spacing={2}>
                                <Chip
                                  icon={<CalendarTodayIcon />}
                                  label={new Date(item.fecha).toLocaleDateString()}
                                  size="small"
                                />
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title={t('playerCardCreator.tecnicaTab.exportPdf')}>
                                    <Button
                                      variant="contained"
                                      startIcon={<PictureAsPdfIcon />}
                                      onClick={() => generatePDF(item)}
                                      fullWidth
                                      sx={{
                                        borderRadius: 2,
                                        textTransform: 'none',
                                      }}
                                    >
                                      PDF
                                    </Button>
                                  </Tooltip>
                                  {canModifyRecord(currentUser, item.usuario_id) && (
                                    <Tooltip title={t('playerCardCreator.tecnicaTab.deleteSheet')}>
                                      <IconButton
                                        color="error"
                                        onClick={() => handleDeleteClick(item)}
                                        sx={{
                                          border: '1px solid',
                                          borderColor: 'error.main',
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Stack>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Paper>
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            </Zoom>
          );
        })}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: { xs: '90%', sm: 400 }
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <DeleteIcon color="error" />
            <Typography variant="h6">{t('playerCardCreator.tecnicaTab.confirmDeleteTitle')}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('playerCardCreator.tecnicaTab.confirmDeleteMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            {t('playerCardCreator.tecnicaTab.cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" autoFocus>
            {t('playerCardCreator.tecnicaTab.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TechnicaTab;